import { resolveActivityIconUrl } from '../config/publicIcons.config'
import DateUtil from '../util/DateUtil'

export type ActivityGroup = 'primary' | 'everyday' | 'partner' | 'bonus'

export interface ActivityArgument {
  id: string
  title: string
  description: string
  unit: string
  unitConversion: number
  icon: string
  alertThreshold: number
  isActive: boolean
  challengeID?: string
  bucketID?: string | null
  bucketName: string | null
  availableFromDate: Date | null
  availableUntilDate: Date | null
  oneTimeUse: boolean
  requiresAmount: boolean
  group: ActivityGroup
  order: number | null
  gateId?: string
}

export default class Activity {
  readonly id: string
  readonly title: string
  readonly description: string
  readonly unit: string
  readonly unitConversion: number
  readonly icon: string
  readonly alertThreshold: number
  readonly isActive: boolean
  readonly challengeID?: string
  readonly bucketID?: string | null
  readonly bucketName: string | null
  readonly availableFromDate: Date | null
  readonly availableUntilDate: Date | null
  readonly oneTimeUse: boolean
  readonly requiresAmount: boolean
  readonly group: ActivityGroup
  readonly order: number | null = null
  readonly gateId?: string

  constructor(arg: ActivityArgument) {
    this.id = arg.id
    this.title = arg.title
    this.description = arg.description
    this.unit = arg.unit
    this.unitConversion = arg.unitConversion
    this.icon = arg.icon
    this.alertThreshold = arg.alertThreshold
    this.isActive = arg.isActive
    this.challengeID = arg.challengeID
    this.bucketID = arg.bucketID ?? null
    this.bucketName = arg.bucketName ?? null
    this.availableFromDate = arg.availableFromDate ?? null
    this.availableUntilDate = arg.availableUntilDate ?? null
    this.oneTimeUse = arg.oneTimeUse ?? false
    this.requiresAmount = arg.requiresAmount ?? true
    this.group = arg.group
    this.order = arg.order ?? null
    this.gateId = arg.gateId
  }

  isAvailableNow(): boolean {
    const now = DateUtil.getNowInEST()
    if (this.availableFromDate != null && now < this.availableFromDate) return false
    if (this.availableUntilDate != null) {
      const startOfDayAfterUntil = new Date(this.availableUntilDate.getTime() + 24 * 60 * 60 * 1000)
      if (now >= startOfDayAfterUntil) return false
    }
    return true
  }

  static fromPG(data: any): Activity {
    const bucketName = data.buckets?.name ?? data.bucket ?? null
    const group = Activity.parseGroup(data.group)

    return new Activity({
      id: data.id,
      title: data.title,
      description: data.description,
      unit: data.unit,
      unitConversion: data.unit_conversion,
      icon: data.icon,
      alertThreshold: data.alert_threshold,
      isActive: data.is_active,
      challengeID: data.challenge_id ?? undefined,
      bucketID: data.bucket_id ?? null,
      bucketName: bucketName ?? null,
      availableFromDate:
        data.available_from_date != null && data.available_from_date !== ''
          ? DateUtil.setTo1201AM(data.available_from_date)
          : null,
      availableUntilDate:
        data.available_until_date != null && data.available_until_date !== ''
          ? DateUtil.setTo1201AM(data.available_until_date)
          : null,
      oneTimeUse: data.one_time_use === true,
      requiresAmount: data.requires_amount !== false,
      group,
      order: data.order != null ? Number(data.order) : null,
      gateId: data.gate_id ?? undefined,
    })
  }

  static parseGroup(value: unknown): ActivityGroup {
    if (value === 'primary' || value === 'everyday' || value === 'partner' || value === 'bonus') {
      return value
    }
    throw new Error(`Invalid activity group: ${String(value)}`)
  }

  getIconColor(): string {
    if (this.group === 'partner') return 'var(--blue-ink)'
    if (this.group === 'bonus') return 'var(--orange)'
    return 'var(--blue)'
  }

  static getDisplayIcon(
    activity: Activity,
  ): { type: 'img'; src: string } | { type: 'iconify'; icon: string } {
    const icon = activity.icon
    if (icon.startsWith('http://') || icon.startsWith('https://')) {
      return { type: 'img', src: icon }
    }
    if (icon.includes(':')) {
      return { type: 'iconify', icon }
    }
    return { type: 'img', src: resolveActivityIconUrl(icon) }
  }
}
